import { useState } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Collapse,
  Divider,
  Group,
  Paper,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { ChevronDown, ChevronUp } from 'react-feather';
import type { CoachCompensationCohort, CoachPaymentRecord } from '../../../types';
import { formatCents, formatDateOnlyRange } from '../../../utils/formatters';
import { parseDateOnly } from '../../../utils/dateHelpers';
import {
  COACH_PAYMENT_TRACK_LABELS,
  compareLineItems,
  getLineItemRateUnit,
  getPaymentNoteColor,
  getPaymentStatusConfig,
  HISTORY_ROW_STYLE,
  isHistoryPayment,
  WRAPPING_BADGE_STYLES,
} from '../../../config/compensationConfig';
import { getAvatarColor } from '../../../utils/avatarHelpers';

/**
 * MED-1383: the plan amount sits in a fixed slot, so amounts line up from row to
 * row however the titles wrap.
 */
const AMOUNT_SLOT_STYLE = {
  flexShrink: 0,
  minWidth: 150,
  textAlign: 'right',
  whiteSpace: 'nowrap',
} as const;

interface CohortCompensationCardProps {
  cohort: CoachCompensationCohort;
}

/**
 * MED-1383: the coach-facing title of a payment row. The API sends `title`. The
 * fallback covers an older API pod and never shows the bookkeeping memo, which held
 * the coach's own name and, on old rows, "Net-10".
 */
function paymentTitle(p: CoachPaymentRecord): string {
  if (p.title) return p.title;
  return p.payment_track === 'flat_rate_monthly' ? 'Group coaching' : 'Event payment';
}

function CohortAvatar({ name }: { name: string }) {
  const initials = name
    .split(/\s+/)
    .map(w => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?';
  return (
    <Avatar size={44} radius="md" color={getAvatarColor(name)}>
      {initials}
    </Avatar>
  );
}

/**
 * Expandable card showing a single cohort's compensation breakdown for the coach portal.
 * Matches mocks 6-9 layout.
 */
export function CohortCompensationCard({ cohort }: CohortCompensationCardProps) {
  const [expanded, setExpanded] = useState(false);

  const cohortStatusBadge = (() => {
    if (cohort.cohort_status === 'live') return { label: 'LIVE', color: 'medleyGreen' };
    if (cohort.cohort_status === 'completed') return { label: 'COMPLETE', color: 'gray' };
    if (cohort.cohort_status === 'wrap_up') return { label: 'WRAP-UP', color: 'gray' };
    return null;
  })();

  return (
    <Paper
      radius="md"
      style={{ border: '1px solid #E8E6E1', background: 'white', overflow: 'hidden' }}
    >
      <UnstyledButton
        onClick={() => setExpanded(e => !e)}
        style={{ width: '100%', padding: 16 }}
      >
        <Group justify="space-between" align="center" wrap="nowrap">
          <Group gap="md" wrap="nowrap" style={{ flex: 1 }}>
            <CohortAvatar name={cohort.cohort_name} />
            <div>
              <Group gap="xs" align="center">
                <Text fw={700} size="md">
                  {cohort.cohort_name}
                </Text>
                {cohortStatusBadge && (
                  <Badge size="xs" color={cohortStatusBadge.color} variant="light">
                    {cohortStatusBadge.label}
                  </Badge>
                )}
              </Group>
              <Text size="xs" c="dimmed">
                {cohort.partner_name ? `${cohort.partner_name} · ` : ''}
                {/* MED-1383: cohort dates are date-only. formatDateRange read them as
                    UTC midnight and showed the day before for a coach west of UTC. */}
                {formatDateOnlyRange(cohort.cohort_start_date, cohort.cohort_end_date)}
              </Text>
            </div>
          </Group>
          {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </Group>
      </UnstyledButton>

      <Collapse in={expanded}>
        <Divider />
        <Box p="lg">
          <Stack gap="lg">
            {/* Compensation Plan */}
            <div>
              <Text
                size="xs"
                fw={700}
                tt="uppercase"
                c="dimmed"
                mb="xs"
                style={{ letterSpacing: 0.3 }}
              >
                Compensation Plan
              </Text>
              {(() => {
                // MED-1383: an estimate is a guaranteed-kind line the CM has not
                // confirmed yet. It keeps the guaranteed layout and amount, with an
                // Estimated badge instead of Guaranteed.
                const isPlanned = (li: (typeof cohort.line_items)[number]) =>
                  !!li.is_guaranteed || !!li.is_estimate;
                // MED-1383: one fixed line order for every coach (compareLineItems).
                const orderedItems = [...cohort.line_items].sort(compareLineItems);
                const guaranteedItems = orderedItems.filter(isPlanned);
                const variableItems = orderedItems.filter(li => !isPlanned(li));
                const months = cohort.program_months ?? 0;
                // The API applies the same rule. Checking the status here too keeps
                // an older API pod from showing an estimate as Guaranteed.
                const isConfirmedGuarantee = (li: (typeof cohort.line_items)[number]) =>
                  !!li.is_guaranteed && cohort.status === 'confirmed';

                return (
                  <Paper
                    radius="md"
                    style={{
                      border: '1px solid #E8E6E1',
                      background: 'white',
                      overflow: 'hidden',
                    }}
                  >
                    {/* Guaranteed items: keep rate × count = total format */}
                    {guaranteedItems.map(li => {
                      const effectiveTotal = li.adjusted_total_cents ?? li.total_cents;
                      // MED-1383: a flat rate line reads per month x months, the
                      // same unit as the monthly payment rows below. Other lines,
                      // or a total that does not split evenly, keep rate x count.
                      const formula =
                        li.payment_track === 'flat_rate_monthly' &&
                        months > 0 &&
                        effectiveTotal % months === 0
                          ? `${formatCents(effectiveTotal / months)} per month × ${months} ${
                              months === 1 ? 'month' : 'months'
                            }`
                          : li.quantity !== null && li.unit_rate_cents !== null
                            ? `${formatCents(li.unit_rate_cents)} × ${li.quantity}`
                            : null;
                      return (
                        <Box
                          key={li.id}
                          p="md"
                          style={{ borderBottom: '1px solid #F0EEE9' }}
                        >
                          <Group justify="space-between" align="center" wrap="nowrap">
                            <div style={{ flex: 1 }}>
                              <Text size="sm" fw={600}>
                                {li.label}
                              </Text>
                              {formula && (
                                <Text size="xs" c="dimmed" mt={2}>
                                  {formula}
                                </Text>
                              )}
                            </div>
                            <Group gap="sm" align="center">
                              <Text size="sm" fw={700} style={AMOUNT_SLOT_STYLE}>
                                {formatCents(effectiveTotal)}
                              </Text>
                              <Badge
                                size="sm"
                                color={isConfirmedGuarantee(li) ? 'medleyGreen' : 'blue'}
                                variant="light"
                                style={{ minWidth: 100, textAlign: 'center' }}
                              >
                                {isConfirmedGuarantee(li) ? 'GUARANTEED' : 'ESTIMATED'}
                              </Badge>
                            </Group>
                          </Group>
                        </Box>
                      );
                    })}
                    {/* Variable items: per-session rate only */}
                    {variableItems.map(li => (
                      <Box
                        key={li.id}
                        p="md"
                        style={{ borderBottom: '1px solid #F0EEE9' }}
                      >
                        <Group justify="space-between" align="center" wrap="nowrap">
                          <div style={{ flex: 1 }}>
                            <Text size="sm" fw={600}>
                              {li.label}
                            </Text>
                            <Text size="xs" c="dimmed" mt={2}>
                              Subject to attendance/completion
                            </Text>
                          </div>
                          <Text size="sm" fw={700} style={AMOUNT_SLOT_STYLE}>
                            {li.unit_rate_cents !== null
                              ? `${formatCents(li.unit_rate_cents)} ${getLineItemRateUnit(li.item_key)}`
                              : formatCents(li.adjusted_total_cents ?? li.total_cents)}
                          </Text>
                        </Group>
                      </Box>
                    ))}
                  </Paper>
                );
              })()}
            </div>

            {/* Payments (E13-7 AC13, MDF-180) */}
            <div>
              <Text
                size="xs"
                fw={700}
                tt="uppercase"
                c="dimmed"
                mb="xs"
                style={{ letterSpacing: 0.3 }}
              >
                Payments
              </Text>
              {(() => {
                // MDF-180: flat-rate monthly payments were invisible to
                // coaches after MED-949 removed their section, so a coach paid
                // only on the monthly track saw an empty record of real money.
                // Both tracks now render in one list.
                //
                // Ascending by process_date matches
                // `ORDER BY bp.process_date ASC` in BillComPaymentRepository,
                // the order the CM-side table already relies on. Both arrives
                // sorted; re-sorting keeps the merge stable.
                // MED-1383: rows on the same date sort by title, so a coach
                // with two groups sees Group 1 before Group 2 every month.
                // Voided rows are history: they sort after the live rows and
                // are dimmed, as in the cohort payment tables.
                //
                // payment_schedule is deliberately excluded: it holds
                // projections for cohorts with no real payments yet, and
                // mixing it in would present estimates as payment records.
                const payments = [
                  ...(cohort.monthly_payments ?? []),
                  ...(cohort.event_payments ?? []),
                ].sort(
                  (a, b) =>
                    Number(isHistoryPayment(a.status)) - Number(isHistoryPayment(b.status)) ||
                    a.process_date.localeCompare(b.process_date) ||
                    paymentTitle(a).localeCompare(paymentTitle(b), undefined, {
                      numeric: true,
                    }),
                );

                if (payments.length === 0) {
                  return (
                    <Paper
                      p="md"
                      radius="md"
                      style={{ border: '1px solid #E8E6E1', background: '#FAFAF8' }}
                    >
                      <Text size="xs" c="dimmed" ta="center">
                        No payments yet.
                      </Text>
                    </Paper>
                  );
                }

                return (
                  <Paper
                    radius="md"
                    style={{
                      border: '1px solid #E8E6E1',
                      background: 'white',
                      overflow: 'hidden',
                    }}
                  >
                    {payments.map((p, idx) => {
                      // getPaymentStatusConfig falls back to Pending for an
                      // unexpected value, and adds the Bill.com date to Scheduled.
                      const statusCfg = getPaymentStatusConfig(
                        p.status,
                        p.billcom_scheduled_date,
                      );
                      const trackCfg = COACH_PAYMENT_TRACK_LABELS[p.payment_track];
                      // process_date is a date-only column. A raw `new Date()`
                      // reads it as UTC midnight and renders the day before for
                      // any coach west of UTC (the MED-1265 sweep missed this
                      // call site).
                      const due = parseDateOnly(p.process_date);
                      return (
                        <Box
                          key={`${p.payment_track}-${p.process_date}-${idx}`}
                          p="md"
                          style={{
                            borderBottom:
                              idx < payments.length - 1 ? '1px solid #F0EEE9' : undefined,
                            ...(isHistoryPayment(p.status) ? HISTORY_ROW_STYLE : {}),
                          }}
                        >
                          <Group justify="space-between" align="center" wrap="nowrap">
                            <div style={{ flex: 1 }}>
                              <Text size="sm" fw={500}>
                                {paymentTitle(p)}
                              </Text>
                              {/* MED-1383: the date is the due date, not the event date, so
                                  it says so. A coach read Jan 15 as the day of a Jan 5 event. */}
                              <Text size="xs" c="dimmed">
                                {due
                                  ? `Due ${due.toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric',
                                    })}`
                                  : '--'}
                              </Text>
                              {p.status === 'failed' && p.decline_reason && (
                                <Text size="xs" c={getPaymentNoteColor(p.status)} mt={2}>
                                  {p.decline_reason}
                                </Text>
                              )}
                            </div>
                            {/* MED-1383: the badges may wrap on a narrow screen. The amount
                                sits in the same fixed slot as the plan rows, at the end, so
                                the amounts line up in one column. */}
                            <Group gap="xs" align="center" justify="flex-end">
                              {/* A voided row is history. It is not a guarantee. */}
                              {trackCfg && !isHistoryPayment(p.status) && (
                                <Badge
                                  size="sm"
                                  color={trackCfg.color}
                                  variant={trackCfg.variant}
                                  styles={WRAPPING_BADGE_STYLES}
                                >
                                  {trackCfg.label}
                                </Badge>
                              )}
                              <Badge
                                size="sm"
                                color={statusCfg.color}
                                variant={statusCfg.variant}
                                styles={WRAPPING_BADGE_STYLES}
                              >
                                {statusCfg.label}
                              </Badge>
                            </Group>
                            <Text size="sm" fw={700} style={AMOUNT_SLOT_STYLE}>
                              {formatCents(p.amount_cents)}
                            </Text>
                          </Group>
                        </Box>
                      );
                    })}
                  </Paper>
                );
              })()}
            </div>

          </Stack>
        </Box>
      </Collapse>
    </Paper>
  );
}
